import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "./api";
import { ymd, formatEventDate, formatTime12 as formatTime, getRsvpLabel as getRsvpLabelBase } from "./utils";
import RecentRsvpsTile from "./RecentRsvpsTile";
import { FaCheck, FaWrench, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaUsers, FaStickyNote, FaMedal, FaShip, FaHistory, FaSitemap, FaExclamationTriangle } from "react-icons/fa";
import { GiCanoe } from "react-icons/gi";
import "./AthleteHomePage.css";
import "./CoachHomePage.css";

const getRsvpLabel = (status) => getRsvpLabelBase(status, "No Response");

export default function CoachHomePage({
  coach,
  onAvailabilityClick,
  onMaintenanceRequestClick,
  onPracticeClick,
  onMaintenanceClick,
  onOyoWorkoutClick,
  onLineupClick,
  refreshKey
}) {
  const [nextEvent, setNextEvent] = useState(null);
  const [loadingNextEvent, setLoadingNextEvent] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [leaderboardTab, setLeaderboardTab] = useState("all");
  const [leaderboardPeriod, setLeaderboardPeriod] = useState("30d");

  const LEADERBOARD_TABS = [
    { key: "all", label: "All" },
    { key: "u19", label: "U19", ageGroups: ["U19", "Open"] },
    { key: "u17", label: "U17", ageGroups: ["U17"] },
    { key: "u16", label: "U16", ageGroups: ["U16"] },
  ];

  const coachTeam = typeof coach?.team === "string" ? coach.team.trim() : "";
  const coachId = coach?.id ? Number(coach.id) : null;

  // Load team leaderboard
  const loadLeaderboard = useCallback(async (tab, period) => {
    if (!coachTeam) return;
    setLoadingLeaderboard(true);
    try {
      const tabDef = LEADERBOARD_TABS.find(t => t.key === tab) || LEADERBOARD_TABS[0];
      let url = `/athlete-stats/leaderboard?team=${encodeURIComponent(coachTeam)}`;
      if (tabDef.ageGroups) {
        url += `&age_groups=${encodeURIComponent(tabDef.ageGroups.join(","))}`;
      }
      if (period === "all") url += `&period=all`;
      const rows = await apiFetch(url);
      setLeaderboard(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error("Failed to load leaderboard:", err);
      setLeaderboard([]);
    } finally {
      setLoadingLeaderboard(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachTeam]);

  // Load next event (or current in-progress event)
  const loadNextEvent = useCallback(async () => {
    if (!coachId || !coachTeam) return;
    setLoadingNextEvent(true);
    try {
      const now = new Date();
      const endDate = new Date(now);
      endDate.setDate(endDate.getDate() + 30);

      const from = ymd(now);
      const to = ymd(endDate);

      const params = new URLSearchParams({ from, to });
      if (coachTeam) params.append("team", coachTeam);

      const [practiceRows, attendanceRows, launchData] = await Promise.all([
        apiFetch(`/practices/range?${params.toString()}`),
        apiFetch(`/attendance/coach/range?user=${coachId}&from=${from}&to=${to}`),
        apiFetch(`/coach-launch-assignments?from=${from}&to=${to}`).catch(() => null),
      ]);

      const practices = Array.isArray(practiceRows) ? practiceRows : [];
      const attendance = Array.isArray(attendanceRows) ? attendanceRows : [];

      const attendanceMap = new Map(
        attendance.map(a => [a.practice_id, a.status])
      );

      // Build launch lookup: practiceId -> launch name for this coach
      const launchAssignments = launchData?.assignments?.[String(coachId)] || {};

      const normalized = practices.map(p => ({
        ...p,
        startTime: p.startTime || p.start_time || p.time,
        endTime: p.endTime || p.end_time,
        squad: p.squad || p.squadName,
        rsvpStatus: attendanceMap.get(p.id) || null,
        launchAssignment: launchAssignments[String(p.id)]?.launch || null,
        hullAssignments: launchAssignments[String(p.id)]?.hulls || [],
      }));

      // Prefer a currently in-progress practice (started but end time hasn't passed)
      const inProgress = normalized.find(p => {
        if (p.state === "off") return false;
        const start = p.date ? new Date(`${p.date}T${p.startTime || "00:00"}`) : null;
        const end = p.date && p.endTime ? new Date(`${p.date}T${p.endTime}`) : null;
        if (!start) return false;
        return start <= now && (!end || end > now);
      });

      const upcoming = !inProgress
        ? normalized
            .filter(p => {
              if (p.state === "off") return false;
              const start = p.date ? new Date(`${p.date}T${p.startTime || "23:59"}`) : null;
              return start && start > now;
            })
            .sort((a, b) => {
              const dateA = new Date(`${a.date}T${a.startTime || "00:00"}`);
              const dateB = new Date(`${b.date}T${b.startTime || "00:00"}`);
              return dateA - dateB;
            })[0]
        : null;

      setNextEvent(inProgress || upcoming || null);
    } catch (err) {
      console.error("Failed to load next event:", err);
      setNextEvent(null);
    } finally {
      setLoadingNextEvent(false);
    }
  }, [coachId, coachTeam]);

  // Load data independently for faster perceived performance
  useEffect(() => {
    if (!coachId || !coachTeam) return;

    loadNextEvent();
    loadLeaderboard("all", "30d");
  }, [coachId, coachTeam, loadNextEvent, loadLeaderboard, refreshKey]);

  function handleLeaderboardTabChange(tabKey) {
    setLeaderboardTab(tabKey);
    loadLeaderboard(tabKey, leaderboardPeriod);
  }

  function handleLeaderboardPeriodChange(period) {
    setLeaderboardPeriod(period);
    loadLeaderboard(leaderboardTab, period);
  }

  function formatNumber(num) {
    return num.toLocaleString();
  }

  function handleNextEventClick(event) {
    if (onPracticeClick && event.id) {
      onPracticeClick(event.id, event.date, event.startTime || event.time, event.squad, event.endTime, event.name, event.location);
    }
  }

  const isInProgress = nextEvent && !nextEvent.cancelled && (() => {
    const start = nextEvent.date ? new Date(`${nextEvent.date}T${nextEvent.startTime || "00:00"}`) : null;
    const end = nextEvent.date && nextEvent.endTime ? new Date(`${nextEvent.date}T${nextEvent.endTime}`) : null;
    const now = new Date();
    return start && start <= now && (!end || end > now);
  })();

  const podiumOrder = [1, 0, 2]; // silver, gold, bronze visual order
  const topThree = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);

  return (
    <div className="coach-home-page">
      {/* Hero: current / next practice */}
      <div className="home-hero">
        <div className="home-hero-top">
          <span className="home-hero-eyebrow">
            <span className="dot" />
            {isInProgress ? "Current Practice" : "Next Event"}
          </span>
          {isInProgress && <span className="home-hero-live">● Live</span>}
        </div>
        {loadingNextEvent ? (
          <div className="home-hero-empty">Loading...</div>
        ) : nextEvent ? (
          <>
            <div className="home-hero-date">
              <FaCalendarAlt style={{ marginRight: 6 }} />
              {formatEventDate(nextEvent.date)}
            </div>
            {nextEvent.cancelled && (
              <div
                className="home-hero-cancelled-banner"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  background: "rgba(179,38,30,0.18)",
                  border: "1px solid var(--danger-border)",
                  borderRadius: 12,
                  padding: "10px 12px",
                  margin: "8px 0",
                  color: "#fde047",
                }}
              >
                <FaExclamationTriangle style={{ marginTop: 2, flexShrink: 0, color: "#ef4444" }} />
                <span>
                  <strong>Practice Cancelled</strong>
                  {nextEvent.cancellationNote ? `: ${nextEvent.cancellationNote}` : ""}
                </span>
              </div>
            )}
            <div className="home-hero-title">{nextEvent.name || "Practice"}</div>
            <div className="home-hero-meta-row">
              <span className="home-hero-chip">
                <FaClock />
                {formatTime(nextEvent.startTime || nextEvent.time)}
                {nextEvent.endTime && ` – ${formatTime(nextEvent.endTime)}`}
              </span>
              {nextEvent.location && (
                <span className="home-hero-chip"><FaMapMarkerAlt />{nextEvent.location}</span>
              )}
              {nextEvent.squad && (
                <span className="home-hero-chip"><FaUsers />{nextEvent.squad}</span>
              )}
              {nextEvent.note && (
                <span className="home-hero-chip"><FaStickyNote />{nextEvent.note}</span>
              )}
              {nextEvent.launchAssignment && (
                <span className="home-hero-chip home-hero-launch">
                  <FaShip />
                  <span className="home-hero-launch-name">{nextEvent.launchAssignment}</span>
                </span>
              )}
              {nextEvent.hullAssignments.map((hull) => (
                <span key={hull} className="home-hero-chip home-hero-hull">
                  <GiCanoe />
                  <span className="home-hero-hull-name">{hull}</span>
                </span>
              ))}
            </div>
            <div className="home-hero-actions">
              {!nextEvent.cancelled && (
                <button
                  className={`home-hero-btn primary rsvp-status-${nextEvent.rsvpStatus ? nextEvent.rsvpStatus.toLowerCase().replace(/\s+/g, '') : 'noresponse'}`}
                  onClick={onAvailabilityClick ? () => onAvailabilityClick(nextEvent.id) : undefined}
                  disabled={!onAvailabilityClick}
                >
                  <FaCheck style={{ marginRight: 6 }} />
                  RSVP ({getRsvpLabel(nextEvent.rsvpStatus)})
                </button>
              )}
              <button
                className="home-hero-btn lineup"
                onClick={onLineupClick ? () => onLineupClick(nextEvent.date, nextEvent.id) : undefined}
                disabled={!onLineupClick}
              >
                <FaSitemap style={{ marginRight: 6 }} />
                Open Lineup
              </button>
              <button
                className="home-hero-btn ghost"
                onClick={onPracticeClick ? () => handleNextEventClick(nextEvent) : undefined}
                disabled={!onPracticeClick}
              >
                Details
              </button>
            </div>
          </>
        ) : (
          <div className="home-hero-empty">No upcoming practices</div>
        )}
      </div>

      {/* Quick actions */}
      <div className="home-quick-actions">
        <button className="home-qa-btn" onClick={onAvailabilityClick ? () => onAvailabilityClick() : undefined} disabled={!onAvailabilityClick}>
          <span className="home-qa-icon" style={{ background: "var(--success-bg)", color: "var(--success)" }}><FaCheck /></span>
          <span className="home-qa-label">Availability</span>
        </button>
        <button className="home-qa-btn" onClick={onMaintenanceRequestClick} disabled={!onMaintenanceRequestClick}>
          <span className="home-qa-icon" style={{ background: "var(--warn-bg)", color: "var(--warn)" }}><FaWrench /></span>
          <span className="home-qa-label">Maintenance</span>
        </button>
        <button
          className="home-qa-btn"
          onClick={onLineupClick ? () => onLineupClick(nextEvent?.date || ymd(new Date())) : undefined}
          disabled={!onLineupClick}
        >
          <span className="home-qa-icon" style={{ background: "rgba(79,140,249,0.16)", color: "var(--primary)" }}><FaSitemap /></span>
          <span className="home-qa-label">Lineups</span>
        </button>
      </div>

      {/* Leaderboard + Recent activity, side by side on wider screens */}
      <div className="home-secondary-grid">
        <div>
          <div className="home-section-label">
            <span className="t"><FaMedal />Team Leaderboard</span>
            <div className="leaderboard-period-toggle">
              <button
                className={`leaderboard-period-btn${leaderboardPeriod === "30d" ? " active" : ""}`}
                onClick={() => handleLeaderboardPeriodChange("30d")}
              >30d</button>
              <button
                className={`leaderboard-period-btn${leaderboardPeriod === "all" ? " active" : ""}`}
                onClick={() => handleLeaderboardPeriodChange("all")}
              >All</button>
            </div>
          </div>
          <div className="home-panel">
            <div className="leaderboard-tabs">
              {LEADERBOARD_TABS.map(tab => (
                <button
                  key={tab.key}
                  className={`leaderboard-tab${leaderboardTab === tab.key ? " active" : ""}`}
                  onClick={() => handleLeaderboardTabChange(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {loadingLeaderboard ? (
              <div className="help">Loading...</div>
            ) : leaderboard.length === 0 ? (
              <div className="help">No workout data yet</div>
            ) : (
              <>
                {topThree.length > 0 && (
                  <div className="home-podium">
                    {podiumOrder.filter(i => topThree[i]).map(i => {
                      const athlete = topThree[i];
                      const slotClass = i === 0 ? "gold" : i === 1 ? "silver" : "bronze";
                      const valueText = athlete.meters > 0
                        ? `${formatNumber(athlete.meters)}m`
                        : `${athlete.workouts} wo`;
                      return (
                        <div key={athlete.id} className={`home-podium-slot ${slotClass}`}>
                          <div className="home-podium-avatar">{i + 1}</div>
                          <div className="home-podium-name">{athlete.name}</div>
                          <div className="home-podium-val">{valueText}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {rest.map((athlete, idx) => (
                  <div key={athlete.id} className="home-lb-row">
                    <span className="home-lb-rank">{idx + 4}</span>
                    <span className="home-lb-name">{athlete.name}</span>
                    <span className="home-lb-val">
                      {athlete.meters > 0
                        ? `${formatNumber(athlete.meters)} m`
                        : `${athlete.workouts} workout${athlete.workouts !== 1 ? "s" : ""}`
                      }
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        <div>
          <div className="home-section-label">
            <span className="t"><FaHistory />Recent Activity</span>
          </div>
          <RecentRsvpsTile
            team={coachTeam}
            className="home-panel home-activity-panel"
            onMaintenanceClick={onMaintenanceClick}
            onPracticeClick={onPracticeClick}
            onOyoWorkoutClick={onOyoWorkoutClick}
            onLineupClick={onLineupClick}
          />
        </div>
      </div>
    </div>
  );
}

